/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Brain, Power, RefreshCw, AlertTriangle, Loader2, ArrowLeft,
  Activity, ClipboardList, Clock, Pill, FileText, BellRing,
  Layers as LayersIcon, ChevronRight, Plus, Save, ShieldCheck,
  Printer,
} from 'lucide-react';

const TABS = [
  { key: 'summary',  label: 'Summary',          icon: Activity },
  { key: 'profile',  label: 'Profile',          icon: ClipboardList },
  { key: 'anchors',  label: 'Medicine Anchors', icon: Pill },
  { key: 'events',   label: 'Event Timeline',   icon: Clock },
  { key: 'schedule', label: "Today's Schedule", icon: ClipboardList },
  { key: 'gate',     label: 'Stability Gate',   icon: ShieldCheck },
  { key: 'layers',   label: 'Phase Ladder',     icon: LayersIcon },
  { key: 'alerts',   label: 'Safety Alerts',    icon: BellRing },
  { key: 'export',   label: 'Doctor Export',    icon: FileText },
];

const GATE_META = {
  green:      { cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30', label: 'Green' },
  yellow:     { cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30',       label: 'Yellow' },
  red:        { cls: 'bg-rose-500/15 text-rose-200 border-rose-500/30',          label: 'Red' },
  severe_red: { cls: 'bg-rose-700/30 text-rose-100 border-rose-700/60',          label: 'Severe red' },
};

const STATE_LABELS = {
  stable_on_day: 'Stable',
  delayed_on: 'Delayed ON',
  no_on_risk: 'No-ON risk',
  early_off: 'Early OFF',
  peak_dyskinesia: 'Peak dyskinesia',
  stress_off: 'Stress-linked OFF',
  meal_interference_day: 'Meal interference',
  gut_positive_motor_neutral: 'Gut+ / motor stable',
  gut_positive_motor_negative: 'Gut+ / motor negative',
  calibration_intolerance: 'Calibration intolerance',
};

const SCHEDULE_STATUS_META = {
  planned:   { cls: 'bg-white/5 text-white/70 border-white/10' },
  held:      { cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  skipped:   { cls: 'bg-rose-500/15 text-rose-200 border-rose-500/30' },
  moved:     { cls: 'bg-sky-500/15 text-sky-200 border-sky-500/30' },
  completed: { cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' },
};

const ALERT_SEVERITY_META = {
  yellow:   'bg-amber-500/15 text-amber-200 border-amber-500/30',
  red:      'bg-rose-500/15 text-rose-200 border-rose-500/30',
  critical: 'bg-rose-700/30 text-rose-100 border-rose-700/60',
};

export default function NeuroFlowSubscriber() {
  const { subscriberId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('summary');
  const [today, setToday] = useState(null);
  const [layers, setLayers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [scheduleVersions, setScheduleVersions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const [todayR, layersR, alertsR, schedR, evR] = await Promise.all([
        axios.get(`${API}/neuroflow/${subscriberId}/today`),
        axios.get(`${API}/neuroflow/${subscriberId}/layers`),
        axios.get(`${API}/neuroflow/${subscriberId}/alerts`),
        axios.get(`${API}/neuroflow/${subscriberId}/schedule-versions?limit=20`),
        axios.get(`${API}/neuroflow/${subscriberId}/events?limit=200`),
      ]);
      setToday(todayR.data);
      setLayers(layersR.data?.items || []);
      setAlerts(alertsR.data?.items || []);
      setScheduleVersions(schedR.data?.items || []);
      setEvents(evR.data?.items || []);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load NeuroFlow data');
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);
  useEffect(() => { reloadAll(); }, [reloadAll]);

  const moduleActive = today?.module_active === true;
  const moduleStatus = today?.module_status || (today?.profile ? 'draft' : 'not_started');

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const r = await axios.post(`${API}/neuroflow/${subscriberId}/recalculate`, {
        reason: 'admin_override',
      });
      if (r.data?.skipped) {
        toast.warning(`Recalculation skipped: ${r.data.reason}`);
      } else {
        toast.success(`Recalculation complete · gate ${r.data.gate_status}`);
      }
      await reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recalculation failed. Please retry.');
    } finally {
      setRecalculating(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <AdminLayout>
        <div className="px-6 py-6 max-w-7xl mx-auto">
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading NeuroFlow…
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (pageError) {
    return (
      <AdminLayout>
        <div className="px-6 py-6 max-w-7xl mx-auto">
          <div
            data-testid="neuroflow-subscriber-error"
            className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
          >
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
            <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={reloadAll}>
              Retry
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div
        data-testid="neuroflow-subscriber-page"
        className="px-6 py-6 max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-5">
          <button
            data-testid="neuroflow-back-btn"
            onClick={() => navigate('/admin/neuroflow')}
            className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Back to NeuroFlow overview
          </button>
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-violet-300" />
            <h1 className="text-2xl font-semibold text-white">
              {today?.profile?.diagnosis_label || 'NeuroFlow Subscriber'}
            </h1>
            <Badge
              data-testid="neuroflow-module-status-badge"
              className={moduleActive
                ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30'
                : 'bg-white/5 text-white/60 border-white/10'}
            >
              {moduleStatus}
            </Badge>
          </div>
          <div className="text-xs text-white/40 font-mono mt-1">{subscriberId}</div>
        </div>

        {/* Activation panel */}
        {!moduleActive && (
          <ActivationPanel
            subscriberId={subscriberId}
            moduleStatus={moduleStatus}
            onActivated={reloadAll}
          />
        )}

        {/* Tabs */}
        <div
          data-testid="neuroflow-tabs"
          className="flex flex-wrap gap-1 border-b border-white/10 mb-4"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`neuroflow-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 border-b-2 transition-colors ${
                  active
                    ? 'border-violet-400 text-violet-200'
                    : 'border-transparent text-white/50 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
          <div className="ml-auto py-1">
            {moduleActive && (
              <Button
                data-testid="neuroflow-recalculate-btn"
                size="sm"
                variant="outline"
                onClick={handleRecalculate}
                disabled={recalculating}
                className="border-violet-500/30 text-violet-200 h-8 text-xs"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${recalculating ? 'animate-spin' : ''}`} />
                Recalculate today
              </Button>
            )}
          </div>
        </div>

        {/* Tab body */}
        <div data-testid={`neuroflow-tab-content-${tab}`}>
          {tab === 'summary'  && <SummaryTab today={today} alerts={alerts} />}
          {tab === 'profile'  && <ProfileTab subscriberId={subscriberId} profile={today?.profile} onSaved={reloadAll} />}
          {tab === 'anchors'  && <AnchorsTab subscriberId={subscriberId} anchors={today?.anchors || []} onChanged={reloadAll} />}
          {tab === 'events'   && <EventsTab events={events} subscriberId={subscriberId} onAdded={reloadAll} />}
          {tab === 'schedule' && <ScheduleTab today={today} versions={scheduleVersions} />}
          {tab === 'gate'     && <GateTab today={today} layers={layers} onAction={reloadAll} subscriberId={subscriberId} />}
          {tab === 'layers'   && <LayersTab subscriberId={subscriberId} layers={layers} onChanged={reloadAll} moduleActive={moduleActive} />}
          {tab === 'alerts'   && <AlertsTab alerts={alerts} subscriberId={subscriberId} onChanged={reloadAll} />}
          {tab === 'export'   && <ExportTab subscriberId={subscriberId} />}
        </div>
      </div>
    </AdminLayout>
  );
}

// ──────────────────────────────────────────────────────────────────
// Activation panel
// ──────────────────────────────────────────────────────────────────
function ActivationPanel({ subscriberId, moduleStatus, onActivated }) {
  const [seedLayers, setSeedLayers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('Activated for Parkinson dynamic execution calibration');

  const activate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/neuroflow/${subscriberId}/activate`, {
        condition_type: 'parkinson',
        seed_layers: seedLayers,
        start_mode: 'baseline_mapping',
        notes,
      });
      toast.success('NeuroFlow module activated.');
      onActivated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Activation failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="neuroflow-activation-panel"
      className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-4 mb-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <Power className="w-4 h-4 text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Activate NeuroFlow</h3>
        <Badge className="bg-white/5 text-white/60 border-white/10 text-[10px]">{moduleStatus}</Badge>
      </div>
      <p className="text-xs text-white/60 mb-3">
        NeuroFlow never auto-creates medicine anchors. After activation, configure
        L-Dopa anchors manually under <strong className="text-white/80">Medicine Anchors</strong>.
      </p>
      <label className="flex items-center gap-2 text-xs text-white/70 mb-2">
        <input
          type="checkbox"
          data-testid="neuroflow-seed-layers-checkbox"
          checked={seedLayers}
          onChange={(e) => setSeedLayers(e.target.checked)}
        />
        Seed default Parkinson layer ladder (idempotent)
      </label>
      <textarea
        value={notes}
        data-testid="neuroflow-activation-notes"
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        className="w-full bg-slate-900/60 border border-white/10 rounded px-3 py-2 text-xs text-white/80 mb-3"
      />
      <Button
        data-testid="neuroflow-activate-btn"
        onClick={activate}
        disabled={busy}
        className="bg-violet-600 hover:bg-violet-500 text-white h-8 text-xs"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Power className="w-3.5 h-3.5 mr-1" />}
        Activate NeuroFlow
      </Button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Summary tab
// ──────────────────────────────────────────────────────────────────
function SummaryTab({ today, alerts }) {
  const snap = today?.snapshot;
  const sched = today?.schedule_version;
  const gate = snap?.gate_status;
  const gm = GATE_META[gate];
  const openAlerts = alerts.filter(a => a.status === 'open');
  return (
    <div data-testid="neuroflow-summary-tab" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Current state" value={STATE_LABELS[snap?.current_state] || snap?.current_state || '—'} />
        <SummaryCard label="Gate" value={gm?.label || '—'} cls={gm?.cls} />
        <SummaryCard label="Stability score" value={snap?.stability_score ?? '—'} />
        <SummaryCard label="Open alerts" value={openAlerts.length} cls={openAlerts.length ? 'bg-rose-500/15 text-rose-200 border-rose-500/30' : ''} />
        <SummaryCard label="Delayed ON today" value={snap?.delayed_on_count ?? '—'} />
        <SummaryCard label="No-ON risk today" value={snap?.no_on_risk_count ?? '—'} />
        <SummaryCard label="Total OFF (min)" value={snap?.total_off_time_min ?? '—'} />
        <SummaryCard label="Dyskinesia peak" value={snap?.dyskinesia_peak_score ?? '—'} />
      </div>
      <div className="rounded border border-white/10 bg-slate-900/40 p-4">
        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Why this decision</div>
        <div data-testid="neuroflow-explainability" className="text-sm text-white/80">
          {snap?.explainability_reason || 'Engine has not run for today yet. Press "Recalculate today" or log an event.'}
        </div>
        {snap?.recommended_action && (
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Recommended action</div>
            <div className="text-sm text-violet-200">{snap.recommended_action}</div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded border border-white/10 bg-slate-900/40 p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Active layer</div>
          <div className="text-sm text-white/90">{today?.active_layer?.layer_name || 'No layer active'}</div>
          {today?.active_layer?.axis && (
            <div className="text-[11px] font-mono text-white/40 mt-0.5">{today.active_layer.axis}</div>
          )}
        </div>
        <div className="rounded border border-white/10 bg-slate-900/40 p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Last recalculation</div>
          <div className="text-sm text-white/90">{sched?.created_at || '—'}</div>
          {sched?.reason && (
            <div className="text-[11px] font-mono text-white/40 mt-0.5">reason: {sched.reason}</div>
          )}
        </div>
      </div>
      {snap?.bowel_status && snap.current_state === 'delayed_on' && snap.bowel_status === 'normal' && (
        <div className="rounded border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-sky-200">
          Gut improved, motor response worsened — review timing/layer.
        </div>
      )}
      {snap?.stress_load_score >= 3 && snap?.current_state === 'delayed_on' && (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
          Stress-linked worsening possible.
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, cls }) {
  return (
    <div className={`rounded-lg border p-3 ${cls || 'bg-slate-900/40 border-white/10'}`}>
      <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className="text-lg font-semibold text-white mt-1">{value}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Profile tab
// ──────────────────────────────────────────────────────────────────
function ProfileTab({ subscriberId, profile, onSaved }) {
  const [form, setForm] = useState(() => ({
    diagnosis_label: profile?.diagnosis_label || '',
    onset_year: profile?.onset_year || '',
    dominant_side: profile?.dominant_side || '',
    symptom_dominance: (profile?.symptom_dominance || []).join(', '),
    disease_duration_years: profile?.disease_duration_years || '',
    known_family_history: profile?.known_family_history || '',
    constipation_before_motor_symptoms: profile?.constipation_before_motor_symptoms || '',
    sleep_quality_baseline: profile?.sleep_quality_baseline || '',
    morning_stiffness_baseline: profile?.morning_stiffness_baseline || '',
    baseline_on_latency_min: profile?.baseline_on_latency_min || '',
    baseline_on_duration_min: profile?.baseline_on_duration_min || '',
    baseline_dyskinesia_score: profile?.baseline_dyskinesia_score || '',
    baseline_off_pattern: profile?.baseline_off_pattern || '',
    baseline_first_off_symptom: profile?.baseline_first_off_symptom || '',
    special_notes: profile?.special_notes || '',
  }));
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!profile) {
      toast.error('Profile not found. Activate NeuroFlow first.');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.symptom_dominance = (payload.symptom_dominance || '')
        .split(',').map(s => s.trim()).filter(Boolean);
      ['onset_year', 'disease_duration_years', 'sleep_quality_baseline',
       'morning_stiffness_baseline', 'baseline_on_latency_min',
       'baseline_on_duration_min', 'baseline_dyskinesia_score']
        .forEach(k => {
          if (payload[k] === '' || payload[k] === null) delete payload[k];
          else payload[k] = Number(payload[k]);
        });
      await axios.put(`${API}/neuroflow/${subscriberId}/profile`, payload);
      toast.success('Profile saved.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!profile) {
    return (
      <div data-testid="neuroflow-profile-empty" className="rounded border border-white/10 bg-slate-900/40 p-6 text-center text-sm text-white/60">
        Activate NeuroFlow to create the Parkinson profile for this subscriber.
      </div>
    );
  }

  return (
    <div data-testid="neuroflow-profile-tab" className="space-y-4">
      <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Diagnosis label" v={form.diagnosis_label} onChange={(v)=>update('diagnosis_label', v)} testId="profile-diagnosis_label" />
          <Field label="Onset year" type="number" v={form.onset_year} onChange={(v)=>update('onset_year', v)} testId="profile-onset_year" />
          <Field label="Dominant side" v={form.dominant_side} onChange={(v)=>update('dominant_side', v)} testId="profile-dominant_side" />
          <Field label="Symptom dominance (comma)" v={form.symptom_dominance} onChange={(v)=>update('symptom_dominance', v)} testId="profile-symptom_dominance" />
          <Field label="Disease duration (years)" type="number" v={form.disease_duration_years} onChange={(v)=>update('disease_duration_years', v)} testId="profile-disease_duration_years" />
          <Field label="Family history" v={form.known_family_history} onChange={(v)=>update('known_family_history', v)} testId="profile-known_family_history" />
          <Field label="Constipation before motor symptoms" v={form.constipation_before_motor_symptoms} onChange={(v)=>update('constipation_before_motor_symptoms', v)} testId="profile-constipation_before_motor_symptoms" />
          <Field label="Sleep quality baseline (0-10)" type="number" v={form.sleep_quality_baseline} onChange={(v)=>update('sleep_quality_baseline', v)} testId="profile-sleep_quality_baseline" />
          <Field label="Morning stiffness baseline (0-10)" type="number" v={form.morning_stiffness_baseline} onChange={(v)=>update('morning_stiffness_baseline', v)} testId="profile-morning_stiffness_baseline" />
          <Field label="Baseline ON latency (min)" type="number" v={form.baseline_on_latency_min} onChange={(v)=>update('baseline_on_latency_min', v)} testId="profile-baseline_on_latency_min" />
          <Field label="Baseline ON duration (min)" type="number" v={form.baseline_on_duration_min} onChange={(v)=>update('baseline_on_duration_min', v)} testId="profile-baseline_on_duration_min" />
          <Field label="Baseline dyskinesia (0-10)" type="number" v={form.baseline_dyskinesia_score} onChange={(v)=>update('baseline_dyskinesia_score', v)} testId="profile-baseline_dyskinesia_score" />
          <Field label="Baseline OFF pattern" v={form.baseline_off_pattern} onChange={(v)=>update('baseline_off_pattern', v)} testId="profile-baseline_off_pattern" />
          <Field label="Baseline first OFF symptom" v={form.baseline_first_off_symptom} onChange={(v)=>update('baseline_first_off_symptom', v)} testId="profile-baseline_first_off_symptom" />
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-white/40">Special notes</label>
          <textarea
            data-testid="profile-special_notes"
            value={form.special_notes}
            onChange={(e)=>update('special_notes', e.target.value)}
            rows={3}
            className="w-full bg-slate-900/60 border border-white/10 rounded px-3 py-2 text-xs text-white/80 mt-1"
          />
        </div>
        <Button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={busy}
          className="mt-4 bg-violet-600 hover:bg-violet-500 text-white h-8 text-xs"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save profile
        </Button>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type='text', testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-white/40">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={v ?? ''}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full bg-slate-900/60 border border-white/10 rounded px-3 py-1.5 text-xs text-white/80 mt-1"
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Anchors tab
// ──────────────────────────────────────────────────────────────────
function AnchorsTab({ subscriberId, anchors, onChanged }) {
  const [adding, setAdding] = useState(false);
  return (
    <div data-testid="neuroflow-anchors-tab" className="space-y-4">
      <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
        NeuroFlow never changes medicine dose quantity. L-Dopa timing can only be
        suggested within doctor-approved flexibility windows. Default L-Dopa
        protected window: <strong>60 min before, 90 min after</strong>.
      </div>
      <div className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Time</th>
              <th className="text-left px-3 py-2 font-medium">Medicine</th>
              <th className="text-left px-3 py-2 font-medium">Type</th>
              <th className="text-left px-3 py-2 font-medium">Strength · Dose</th>
              <th className="text-left px-3 py-2 font-medium">Protected (b/a)</th>
              <th className="text-left px-3 py-2 font-medium">Flex (b/a)</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {anchors.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-4 text-center text-white/40">No active anchors.</td></tr>
            )}
            {anchors.map((a) => (
              <AnchorRow key={a.id} subscriberId={subscriberId} anchor={a} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
      {!adding && (
        <Button
          data-testid="neuroflow-add-anchor-btn"
          onClick={() => setAdding(true)}
          size="sm"
          className="bg-violet-600 hover:bg-violet-500 text-white h-8 text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add medicine anchor
        </Button>
      )}
      {adding && (
        <AnchorForm
          subscriberId={subscriberId}
          onSaved={() => { setAdding(false); onChanged(); }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
}

function AnchorRow({ subscriberId, anchor, onChanged }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <tr>
        <td colSpan={8} className="px-3 py-2 bg-slate-950/40">
          <AnchorForm
            subscriberId={subscriberId}
            anchor={anchor}
            onSaved={() => { setEditing(false); onChanged(); }}
            onCancel={() => setEditing(false)}
          />
        </td>
      </tr>
    );
  }
  const remove = async () => {
    try {
      await axios.delete(`${API}/neuroflow/${subscriberId}/anchors/${anchor.id}`);
      toast.success('Anchor deactivated.');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };
  return (
    <tr data-testid={`anchor-row-${anchor.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
      <td className="px-3 py-2 font-mono text-[11px] text-white/80">{anchor.planned_time}</td>
      <td className="px-3 py-2 text-white/90">{anchor.medicine_name}</td>
      <td className="px-3 py-2 text-white/70 font-mono text-[11px]">{anchor.medicine_type}</td>
      <td className="px-3 py-2 text-white/70">
        {anchor.strength_label || '—'}
        {anchor.dose_label ? ` · ${anchor.dose_label}` : ''}
      </td>
      <td className="px-3 py-2 text-white/70 font-mono text-[11px]">
        {anchor.protected_window_before_min}/{anchor.protected_window_after_min} min
      </td>
      <td className="px-3 py-2 text-white/70 font-mono text-[11px]">
        {anchor.doctor_approved_flexibility_enabled
          ? `${anchor.flexibility_before_min}/${anchor.flexibility_after_min} min`
          : 'off'}
      </td>
      <td className="px-3 py-2">
        {anchor.active ? (
          <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-500/30 text-[10px]">Active</Badge>
        ) : (
          <Badge className="bg-white/5 text-white/40 border-white/10 text-[10px]">Inactive</Badge>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex gap-1 justify-end">
          <Button data-testid={`anchor-edit-${anchor.id}`} size="sm" variant="outline" className="border-white/15 h-6 px-2 text-[10px]" onClick={() => setEditing(true)}>Edit</Button>
          {anchor.active && (
            <Button data-testid={`anchor-delete-${anchor.id}`} size="sm" variant="outline" className="border-rose-500/30 text-rose-200 h-6 px-2 text-[10px]" onClick={remove}>Deactivate</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AnchorForm({ subscriberId, anchor, onSaved, onCancel }) {
  const isEdit = !!anchor;
  const [form, setForm] = useState(() => ({
    medicine_name: anchor?.medicine_name || '',
    medicine_type: anchor?.medicine_type || 'levodopa',
    strength_label: anchor?.strength_label || '',
    dose_label: anchor?.dose_label || '',
    planned_time: anchor?.planned_time || '10:00',
    sequence_number: anchor?.sequence_number ?? 1,
    protected_window_before_min: anchor?.protected_window_before_min ?? 60,
    protected_window_after_min: anchor?.protected_window_after_min ?? 90,
    doctor_approved_flexibility_enabled: anchor?.doctor_approved_flexibility_enabled || false,
    flexibility_before_min: anchor?.flexibility_before_min ?? 0,
    flexibility_after_min: anchor?.flexibility_after_min ?? 0,
    rescue_dose_allowed: anchor?.rescue_dose_allowed || false,
  }));
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ['sequence_number','protected_window_before_min','protected_window_after_min',
       'flexibility_before_min','flexibility_after_min']
       .forEach(k => { payload[k] = Number(payload[k] || 0); });
      if (isEdit) {
        delete payload.medicine_type;  // backend forbids change
        await axios.put(`${API}/neuroflow/${subscriberId}/anchors/${anchor.id}`, payload);
        toast.success('Anchor updated.');
      } else {
        await axios.post(`${API}/neuroflow/${subscriberId}/anchors`, payload);
        toast.success('Anchor created.');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="anchor-form" className="rounded border border-white/10 bg-slate-900/40 p-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Medicine name" v={form.medicine_name} onChange={(v)=>update('medicine_name', v)} testId="anchor-form-medicine_name" />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/40">Medicine type</label>
          <select
            data-testid="anchor-form-medicine_type"
            disabled={isEdit}
            value={form.medicine_type}
            onChange={(e)=>update('medicine_type', e.target.value)}
            className="w-full bg-slate-900/60 border border-white/10 rounded px-3 py-1.5 text-xs text-white/80 mt-1 disabled:opacity-60"
          >
            <option value="levodopa">levodopa</option>
            <option value="dopamine_agonist">dopamine_agonist</option>
            <option value="mao_b_inhibitor">mao_b_inhibitor</option>
            <option value="anticholinergic">anticholinergic</option>
            <option value="other">other</option>
          </select>
        </div>
        <Field label="Planned time (HH:MM)" v={form.planned_time} onChange={(v)=>update('planned_time', v)} testId="anchor-form-planned_time" />
        <Field label="Strength label" v={form.strength_label} onChange={(v)=>update('strength_label', v)} testId="anchor-form-strength_label" />
        <Field label="Dose label" v={form.dose_label} onChange={(v)=>update('dose_label', v)} testId="anchor-form-dose_label" />
        <Field label="Sequence number" type="number" v={form.sequence_number} onChange={(v)=>update('sequence_number', v)} testId="anchor-form-sequence_number" />
        <Field label="Protected before (min)" type="number" v={form.protected_window_before_min} onChange={(v)=>update('protected_window_before_min', v)} testId="anchor-form-protected_before" />
        <Field label="Protected after (min)" type="number" v={form.protected_window_after_min} onChange={(v)=>update('protected_window_after_min', v)} testId="anchor-form-protected_after" />
        <label className="flex items-center gap-2 text-xs text-white/70 mt-5">
          <input
            type="checkbox"
            data-testid="anchor-form-flex-enabled"
            checked={form.doctor_approved_flexibility_enabled}
            onChange={(e)=>update('doctor_approved_flexibility_enabled', e.target.checked)}
          />
          Doctor-approved flexibility
        </label>
        <Field label="Flexibility before (min)" type="number" v={form.flexibility_before_min} onChange={(v)=>update('flexibility_before_min', v)} testId="anchor-form-flex_before" />
        <Field label="Flexibility after (min)" type="number" v={form.flexibility_after_min} onChange={(v)=>update('flexibility_after_min', v)} testId="anchor-form-flex_after" />
        <label className="flex items-center gap-2 text-xs text-white/70 mt-5">
          <input
            type="checkbox"
            data-testid="anchor-form-rescue"
            checked={form.rescue_dose_allowed}
            onChange={(e)=>update('rescue_dose_allowed', e.target.checked)}
          />
          Rescue dose allowed (admin-configured only)
        </label>
      </div>
      <div className="flex gap-2 mt-4">
        <Button data-testid="anchor-form-save" onClick={save} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 h-8 text-xs">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          {isEdit ? 'Save changes' : 'Create anchor'}
        </Button>
        <Button data-testid="anchor-form-cancel" onClick={onCancel} size="sm" variant="outline" className="border-white/15 h-8 text-xs">Cancel</Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Events tab
// ──────────────────────────────────────────────────────────────────
const EVENT_TYPES = [
  'medicine_taken','on_started','on_quality',
  'dyskinesia_started','dyskinesia_score',
  'wearing_off_started','full_off',
  'meal_taken','stress_activity','bowel_done',
  'sleep_quality','morning_state','unusual_internal_signal',
];

function EventsTab({ events, subscriberId, onAdded }) {
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const filtered = useMemo(
    () => events.filter(e => !filter || e.event_type === filter),
    [events, filter],
  );
  return (
    <div data-testid="neuroflow-events-tab" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          data-testid="events-filter-type"
          value={filter}
          onChange={(e)=>setFilter(e.target.value)}
          className="bg-slate-900/60 border border-white/10 rounded px-3 py-1.5 text-xs text-white/80"
        >
          <option value="">All event types</option>
          {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button
          data-testid="events-add-btn"
          size="sm"
          onClick={() => setAdding(true)}
          className="bg-violet-600 hover:bg-violet-500 h-8 text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add event
        </Button>
      </div>
      {adding && (
        <EventForm
          subscriberId={subscriberId}
          onSaved={() => { setAdding(false); onAdded(); }}
          onCancel={() => setAdding(false)}
        />
      )}
      <div className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden max-h-[500px] overflow-y-auto">
        {filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-white/40 text-xs">No events.</div>
        )}
        <ul className="divide-y divide-white/5">
          {filtered.map((e) => (
            <li
              key={e.id}
              data-testid={`event-row-${e.id}`}
              className="px-3 py-2 hover:bg-white/[0.02] flex items-start gap-3"
            >
              <div className="text-[11px] font-mono text-white/40 w-44 flex-shrink-0">
                {e.event_time}
              </div>
              <div className="flex-1">
                <div className="text-xs text-white/90">
                  {e.event_type}
                  {e.severity_score != null && (
                    <span className="ml-2 text-[10px] text-amber-200">sev {e.severity_score}</span>
                  )}
                  {e.meal_type && <span className="ml-2 text-[10px] text-sky-200">{e.meal_type}</span>}
                  {e.stress_type && <span className="ml-2 text-[10px] text-rose-200">{e.stress_type}</span>}
                </div>
                {e.note && <div className="text-[11px] text-white/50 mt-0.5">{e.note}</div>}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-white/30">{e.source}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function EventForm({ subscriberId, onSaved, onCancel }) {
  const [eventType, setEventType] = useState('medicine_taken');
  const [note, setNote] = useState('');
  const [severity, setSeverity] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { event_type: eventType, note: note || undefined };
      if (severity !== '') payload.severity_score = Number(severity);
      await axios.post(`${API}/neuroflow/${subscriberId}/events`, payload);
      toast.success('Event logged.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="event-form" className="rounded border border-white/10 bg-slate-900/40 p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
      <select
        data-testid="event-form-type"
        value={eventType}
        onChange={(e)=>setEventType(e.target.value)}
        className="bg-slate-900/60 border border-white/10 rounded px-3 py-1.5 text-xs text-white/80"
      >
        {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <input
        data-testid="event-form-severity"
        type="number" min="0" max="10" placeholder="Severity 0-10"
        value={severity}
        onChange={(e)=>setSeverity(e.target.value)}
        className="bg-slate-900/60 border border-white/10 rounded px-3 py-1.5 text-xs text-white/80"
      />
      <input
        data-testid="event-form-note"
        placeholder="Note (optional)"
        value={note}
        onChange={(e)=>setNote(e.target.value)}
        className="bg-slate-900/60 border border-white/10 rounded px-3 py-1.5 text-xs text-white/80"
      />
      <div className="flex gap-2">
        <Button data-testid="event-form-save" onClick={submit} disabled={busy} size="sm" className="flex-1 bg-violet-600 hover:bg-violet-500 h-8 text-xs">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
        </Button>
        <Button data-testid="event-form-cancel" onClick={onCancel} size="sm" variant="outline" className="border-white/15 h-8 text-xs">Cancel</Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Schedule tab
// ──────────────────────────────────────────────────────────────────
function ScheduleTab({ today, versions }) {
  const sched = today?.schedule_version;
  if (!sched) {
    return (
      <div data-testid="neuroflow-schedule-empty" className="rounded border border-white/10 bg-slate-900/40 p-6 text-center text-sm text-white/60">
        No schedule generated yet today. Click <strong>Recalculate today</strong> after the subscriber logs events.
      </div>
    );
  }
  return (
    <div data-testid="neuroflow-schedule-tab" className="space-y-4">
      <div className="rounded border border-white/10 bg-slate-900/40 p-3">
        <div className="text-[10px] uppercase tracking-wider text-white/40">Latest version</div>
        <div className="text-sm text-white/90">
          v{sched.version_number} · reason: <span className="font-mono">{sched.reason}</span>
        </div>
        <div className="text-[11px] text-white/40 font-mono mt-0.5">{sched.created_at}</div>
        {sched.audit_note && (
          <div className="text-[11px] text-white/50 mt-1">{sched.audit_note}</div>
        )}
      </div>
      <div className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Planned</th>
              <th className="text-left px-3 py-2 font-medium">Adjusted</th>
              <th className="text-left px-3 py-2 font-medium">Type</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {sched.schedule_items.map((it) => {
              const sm = SCHEDULE_STATUS_META[it.status] || SCHEDULE_STATUS_META.planned;
              const isMedAnchor = it.item_type === 'medicine_anchor';
              return (
                <tr
                  key={it.item_id}
                  data-testid={`schedule-item-${it.item_id}`}
                  className={`border-b border-white/5 ${isMedAnchor ? 'bg-violet-500/5' : ''}`}
                >
                  <td className="px-3 py-2 font-mono text-[11px] text-white/80">{it.planned_time || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-white/80">{it.adjusted_time || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-white/80">{it.item_type}</span>
                    {isMedAnchor && (
                      <ShieldCheck className="inline w-3 h-3 ml-1.5 text-violet-300" title="Medicine anchor — never moved by engine" />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[10px] ${sm.cls}`}>{it.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-white/60" title={it.reason}>
                    {it.reason || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {versions.length > 1 && (
        <details className="rounded border border-white/10 bg-slate-900/40 p-3 text-xs">
          <summary className="cursor-pointer text-white/60">All schedule versions ({versions.length})</summary>
          <ul className="mt-2 space-y-1">
            {versions.map(v => (
              <li key={v.id} className="flex items-center gap-3">
                <span className="font-mono text-white/40">{v.date}</span>
                <span className="font-mono text-white/60">v{v.version_number}</span>
                <span className="text-white/50">{v.reason}</span>
                <span className="text-[10px] text-white/30 ml-auto">{v.created_at}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Gate tab
// ──────────────────────────────────────────────────────────────────
function GateTab({ today, layers, onAction, subscriberId }) {
  const snap = today?.snapshot;
  const gate = snap?.gate_status;
  const gm = GATE_META[gate] || GATE_META.green;

  const action = async (slug, body) => {
    try {
      await axios.post(`${API}/neuroflow/${subscriberId}/actions/${slug}`, body || {});
      toast.success(`Action '${slug}' applied.`);
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    }
  };

  const activeLayer = layers.find(L => L.status === 'active');
  return (
    <div data-testid="neuroflow-gate-tab" className="space-y-4">
      <div className={`rounded-lg border p-4 ${gm.cls}`}>
        <div className="text-[10px] uppercase tracking-wider opacity-70">Gate</div>
        <div className="text-2xl font-semibold mt-1">{gm.label}</div>
        <div className="text-xs mt-2">{snap?.recommended_action || 'Engine has not run yet.'}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryCard label="ON latency (first)" value={snap?.first_on_latency_min ?? '—'} />
        <SummaryCard label="ON latency (avg)" value={snap?.avg_on_latency_min ?? '—'} />
        <SummaryCard label="Total ON (min)" value={snap?.total_on_time_min ?? '—'} />
        <SummaryCard label="Total OFF (min)" value={snap?.total_off_time_min ?? '—'} />
        <SummaryCard label="Delayed ON" value={snap?.delayed_on_count ?? 0} />
        <SummaryCard label="No-ON risk" value={snap?.no_on_risk_count ?? 0} />
        <SummaryCard label="Dyskinesia peak" value={snap?.dyskinesia_peak_score ?? 0} />
        <SummaryCard label="Stress load" value={snap?.stress_load_score ?? 0} />
        <SummaryCard label="Bowel" value={snap?.bowel_status || '—'} />
        <SummaryCard label="Sleep" value={snap?.sleep_score ?? '—'} />
        <SummaryCard label="Morning stiffness" value={snap?.morning_stiffness_score ?? '—'} />
        <SummaryCard label="Meal interference" value={snap?.meal_interference_flag ? 'Yes' : 'No'} />
      </div>
      <div className="rounded border border-white/10 bg-slate-900/40 p-4">
        <div className="text-[10px] uppercase tracking-wider text-white/40">Why this decision</div>
        <div className="text-sm text-white/80 mt-1">{snap?.explainability_reason || '—'}</div>
      </div>
      <div className="rounded border border-white/10 bg-slate-900/40 p-4">
        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Admin actions</div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="gate-approve-next-layer"
            size="sm"
            onClick={() => action('approve-next-layer')}
            className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs"
          >
            Approve next layer
          </Button>
          <Button
            data-testid="gate-hold-current-layer"
            size="sm"
            variant="outline"
            onClick={() => {
              const reason = window.prompt('Hold reason?') || '';
              action('hold-current-layer', { hold_reason: reason });
            }}
            className="border-amber-500/30 text-amber-200 h-8 text-xs"
            disabled={!activeLayer}
          >
            Hold current layer
          </Button>
          <Button
            data-testid="gate-rollback-newest"
            size="sm"
            variant="outline"
            onClick={() => {
              const reason = window.prompt('Rollback reason?') || '';
              action('rollback-newest-layer', { rollback_reason: reason });
            }}
            className="border-rose-500/30 text-rose-200 h-8 text-xs"
          >
            Rollback newest layer
          </Button>
          <Button
            data-testid="gate-pause-module"
            size="sm"
            variant="outline"
            onClick={() => action('pause-module', { to_status: 'paused' })}
            className="border-white/15 text-white/70 h-8 text-xs"
          >
            Pause module
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Layers tab
// ──────────────────────────────────────────────────────────────────
function LayersTab({ subscriberId, layers, onChanged, moduleActive }) {
  const seed = async () => {
    try {
      await axios.post(`${API}/neuroflow/${subscriberId}/seed-default-layers`, {});
      toast.success('Default layers seeded.');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed.');
    }
  };
  const setStatus = async (layerId, payload) => {
    try {
      await axios.put(`${API}/neuroflow/${subscriberId}/layers/${layerId}`, payload);
      toast.success('Layer updated.');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  return (
    <div data-testid="neuroflow-layers-tab" className="space-y-3">
      {layers.length === 0 && (
        <div className="rounded border border-white/10 bg-slate-900/40 p-6 text-center text-sm text-white/60">
          No phase ladder seeded yet.
          {moduleActive && (
            <div className="mt-3">
              <Button
                data-testid="layers-seed-default-btn"
                size="sm"
                onClick={seed}
                className="bg-violet-600 hover:bg-violet-500 h-8 text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Seed default Parkinson ladder
              </Button>
            </div>
          )}
        </div>
      )}
      {layers.map((L) => (
        <div
          key={L.id}
          data-testid={`layer-row-${L.layer_code}`}
          className="rounded border border-white/10 bg-slate-900/40 p-3 flex items-start gap-4"
        >
          <div className="text-2xl font-semibold text-white/30 w-6 text-center">{L.layer_order}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm text-white/90">{L.layer_name}</div>
              <Badge className="bg-white/5 text-white/60 border-white/10 text-[10px] font-mono">{L.axis}</Badge>
              <Badge className={`text-[10px] ${
                {active: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
                 stable: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
                 held: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
                 rolled_back: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
                 completed: 'bg-violet-500/15 text-violet-200 border-violet-500/30',
                 not_started: 'bg-white/5 text-white/40 border-white/10'}[L.status] || 'bg-white/5'
              }`}>{L.status}</Badge>
            </div>
            <div className="text-[11px] font-mono text-white/40">{L.layer_code}</div>
            <div className="text-[10px] text-white/40 mt-1">
              start: {L.start_date || '—'} · stable: {L.stable_date || '—'} · linked items: {L.linked_protocol_item_ids?.length || 0}
            </div>
            {(L.hold_reason || L.rollback_reason) && (
              <div className="text-[11px] text-amber-200 mt-1">{L.hold_reason || L.rollback_reason}</div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            {L.status === 'not_started' && (
              <Button data-testid={`layer-${L.layer_code}-activate`} size="sm" variant="outline" className="border-emerald-500/30 text-emerald-200 h-7 text-[10px]" onClick={() => setStatus(L.id, { status: 'active', start_date: new Date().toISOString().slice(0, 10) })}>Activate</Button>
            )}
            {L.status === 'active' && (
              <>
                <Button data-testid={`layer-${L.layer_code}-hold`} size="sm" variant="outline" className="border-amber-500/30 text-amber-200 h-7 text-[10px]" onClick={() => setStatus(L.id, { status: 'held', hold_reason: window.prompt('Hold reason?') || '' })}>Hold</Button>
                <Button data-testid={`layer-${L.layer_code}-complete`} size="sm" variant="outline" className="border-violet-500/30 text-violet-200 h-7 text-[10px]" onClick={() => setStatus(L.id, { status: 'completed' })}>Complete</Button>
              </>
            )}
            {(L.status === 'held' || L.status === 'rolled_back') && (
              <Button data-testid={`layer-${L.layer_code}-reactivate`} size="sm" variant="outline" className="border-emerald-500/30 text-emerald-200 h-7 text-[10px]" onClick={() => setStatus(L.id, { status: 'active' })}>Re-activate</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Alerts tab
// ──────────────────────────────────────────────────────────────────
function AlertsTab({ alerts, subscriberId, onChanged }) {
  const patch = async (alertId, status) => {
    try {
      await axios.patch(`${API}/neuroflow/${subscriberId}/alerts/${alertId}/status`, { to_status: status });
      toast.success('Alert updated.');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  if (alerts.length === 0) {
    return (
      <div data-testid="neuroflow-alerts-empty" className="rounded border border-white/10 bg-slate-900/40 p-6 text-center text-sm text-white/60">
        No safety alerts.
      </div>
    );
  }
  return (
    <div data-testid="neuroflow-alerts-tab" className="space-y-2">
      {alerts.map(a => (
        <div
          key={a.id}
          data-testid={`alert-row-${a.id}`}
          className={`rounded border p-3 ${ALERT_SEVERITY_META[a.severity] || 'bg-white/5 border-white/10'}`}
        >
          <div className="flex items-start gap-3">
            <BellRing className="w-4 h-4 mt-0.5" />
            <div className="flex-1">
              <div className="text-xs font-semibold">
                {a.alert_type} <span className="opacity-50">· {a.severity}</span>
              </div>
              <div className="text-xs mt-0.5 opacity-80">{a.message}</div>
              <div className="text-[10px] mt-1 opacity-50 font-mono">{a.created_at} · {a.status}</div>
            </div>
            {a.status === 'open' && (
              <div className="flex flex-col gap-1">
                <Button data-testid={`alert-ack-${a.id}`} size="sm" variant="outline" className="border-white/15 h-6 px-2 text-[10px]" onClick={()=>patch(a.id, 'acknowledged')}>Ack</Button>
                <Button data-testid={`alert-resolve-${a.id}`} size="sm" variant="outline" className="border-white/15 h-6 px-2 text-[10px]" onClick={()=>patch(a.id, 'resolved')}>Resolve</Button>
              </div>
            )}
            {a.status === 'acknowledged' && (
              <Button data-testid={`alert-resolve-${a.id}`} size="sm" variant="outline" className="border-white/15 h-6 px-2 text-[10px]" onClick={()=>patch(a.id, 'resolved')}>Resolve</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Doctor Export tab
// ──────────────────────────────────────────────────────────────────
function ExportTab({ subscriberId }) {
  return (
    <div data-testid="neuroflow-export-tab" className="rounded border border-white/10 bg-slate-900/40 p-6 text-center">
      <FileText className="w-8 h-8 mx-auto text-violet-300 mb-3" />
      <div className="text-sm text-white/80 mb-2">Doctor Export — printable HTML</div>
      <div className="text-xs text-white/50 mb-4">
        Opens a clean print-ready report covering profile, anchors, events,
        snapshots, schedule changes and safety alerts.
      </div>
      <Link to={`/admin/neuroflow/${subscriberId}/doctor-export`} data-testid="open-doctor-export">
        <Button size="sm" className="bg-violet-600 hover:bg-violet-500 h-8 text-xs">
          <Printer className="w-3.5 h-3.5 mr-1" /> Open printable export
        </Button>
      </Link>
    </div>
  );
}
